import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';

/* ---------- shared bits ---------- */

const STATUS_STYLES: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-sky-100 text-sky-800',
  assigned: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-amber-100 text-amber-800',
  awaiting_citizen: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-700',
  reopened: 'bg-orange-100 text-orange-800',
  duplicate: 'bg-gray-100 text-gray-500',
};
const CLOSED = ['resolved', 'closed', 'duplicate'];
const FOLLOWUP_TYPES = [
  ['comment', 'Comment'],
  ['query', 'Query to department'],
  ['site_visit', 'Site visit'],
  ['escalation', 'Escalation'],
] as const;

const StatusChip: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status || ''] || 'bg-gray-100 text-gray-600'}`}>
    {(status || 'unknown').replaceAll('_', ' ')}
  </span>
);

const Kpi: React.FC<{ label: string; value: React.ReactNode; sub?: string; tone?: string }> = ({ label, value, sub, tone }) => (
  <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone || 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
  </div>
);

/* 12-month stacked trend, pure SVG (no chart deps) */
const TrendChart: React.FC<{ monthly: any[] }> = ({ monthly }) => {
  const months = useMemo(() => {
    const map = new Map<string, { total: number; resolved: number }>();
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      map.set(`${d.getFullYear()}-${d.getMonth()}`, { total: 0, resolved: 0 });
    }
    (monthly || []).forEach((row) => {
      const d = new Date(row.report_month);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const entry = map.get(key);
      if (!entry) return;
      const n = Number(row.count) || 0;
      entry.total += n;
      if (CLOSED.includes(row.status)) entry.resolved += n;
    });
    return Array.from(map.entries()).map(([key, v]) => {
      const [y, m] = key.split('-').map(Number);
      return { label: new Date(y, m, 1).toLocaleString(undefined, { month: 'short' }), ...v };
    });
  }, [monthly]);

  const max = Math.max(1, ...months.map((m) => m.total));
  const W = 720, H = 160, pad = 24, bw = (W - pad * 2) / 12;

  return (
    <svg viewBox={`0 0 ${W} ${H + 24}`} role="img" aria-label="Reports per month for the last 12 months" className="w-full">
      {months.map((m, i) => {
        const x = pad + i * bw;
        const hTotal = (m.total / max) * H;
        const hRes = (m.resolved / max) * H;
        return (
          <g key={i}>
            <rect x={x + 4} y={H - hTotal} width={bw - 8} height={hTotal} rx={3} className="fill-blue-200" />
            <rect x={x + 4} y={H - hRes} width={bw - 8} height={hRes} rx={3} className="fill-green-500" />
            {m.total > 0 && (
              <text x={x + bw / 2} y={H - hTotal - 4} textAnchor="middle" className="fill-gray-500 text-[10px]">{m.total}</text>
            )}
            <text x={x + bw / 2} y={H + 16} textAnchor="middle" className="fill-gray-500 text-[10px]">{m.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ---------- page ---------- */

const WardCouncillorPortal: React.FC = () => {
  const { user } = useAuth();
  const isCouncillor = user?.role === 'councillor';

  const [ward, setWard] = useState(() => localStorage.getItem('councillorWard') || '1');
  const [data, setData] = useState<any>();
  const [followups, setFollowups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // case table controls
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState('');
  const [followupDraft, setFollowupDraft] = useState({ type: 'comment', body: '' });
  const [savingFollowup, setSavingFollowup] = useState(false);

  // notify residents
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notify, setNotify] = useState({ title: '', body: '' });
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const wardParam = isCouncillor ? '' : `?ward=${ward}`;
      const [dash, fups]: any[] = await Promise.all([
        apiService.get(`/portal/councillor/dashboard${wardParam}`),
        apiService.get(`/portal/councillor/followups${wardParam}`),
      ]);
      setData(dash.data);
      setFollowups(fups.data || []);
      if (!isCouncillor) localStorage.setItem('councillorWard', ward);
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to load this ward.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // load on mount; refresh is manual

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(''), 4000); };

  const reports: any[] = data?.reports || [];
  const open = reports.filter((r) => !CLOSED.includes(r.status));
  const resolved = reports.filter((r) => CLOSED.includes(r.status) && r.status !== 'duplicate');
  const resolvedRate = reports.length ? Math.round((resolved.length / reports.length) * 100) : 0;
  const breached = (data?.overdue || []).filter((o: any) => o.breached_at || new Date(o.resolution_due_at) < new Date());
  const openFollowups = followups.filter((f) => f.status === 'open');

  const statuses = useMemo(() => Array.from(new Set(reports.map((r) => r.status))).sort(), [reports]);
  const visible = useMemo(() => reports.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [r.report_number, r.title, r.address, r.priority].some((v: any) => String(v || '').toLowerCase().includes(q));
  }), [reports, query, statusFilter]);

  const followupsFor = (reportId: string) => followups.filter((f) => f.report_id === reportId);

  const addFollowup = async (reportId: string) => {
    if (!followupDraft.body.trim()) return;
    setSavingFollowup(true);
    try {
      await apiService.post(`/portal/councillor/reports/${reportId}/followups`, followupDraft);
      setFollowupDraft({ type: 'comment', body: '' });
      const fups: any = await apiService.get('/portal/councillor/followups');
      setFollowups(fups.data || []);
      flash('Follow-up recorded.');
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to save the follow-up.');
    } finally {
      setSavingFollowup(false);
    }
  };

  const setFollowupStatus = async (id: string, status: string) => {
    try {
      await apiService.patch(`/portal/councillor/followups/${id}`, { status });
      setFollowups((rows) => rows.map((f) => (f.id === id ? { ...f, status } : f)));
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to update the follow-up.');
    }
  };

  const sendNotification = async () => {
    if (!notify.title.trim() || !notify.body.trim()) return;
    if (!window.confirm(`Send this notification to every registered resident of ward ${data?.wardNumber}?`)) return;
    setSending(true);
    try {
      const r: any = await apiService.post('/portal/councillor/notify', notify);
      setNotify({ title: '', body: '' });
      setNotifyOpen(false);
      flash(`Notification sent to ${r.data?.successCount ?? r.data?.sent ?? 'ward'} residents.`);
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to send the notification.');
    } finally {
      setSending(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['Reference', 'Issue', 'Priority', 'Status', 'Reported', 'Follow-ups'],
      ...reports.map((r: any) => [r.report_number, r.title, r.priority, r.status, r.created_at, followupsFor(r.id).length]),
    ];
    const blob = new Blob([rows.map((x) => x.map((v) => `"${String(v ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ward-${data.wardNumber}-cases.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={data ? `Ward ${data.wardNumber}` : 'Ward Councillor Portal'}
        subtitle="Cases, SLA risk, outages and spend for your ward — with follow-ups and resident notifications."
        actions={
          <>
            {!isCouncillor && (
              <label className="text-sm text-gray-600 dark:text-gray-400">Ward
                <input aria-label="Ward number" value={ward} onChange={(e) => setWard(e.target.value.replace(/\D/g, ''))}
                  className="ml-2 w-24 rounded-sm border px-3 py-2 text-gray-900 dark:text-gray-100" inputMode="numeric" />
              </label>
            )}
            <Button variant="primary" disabled={loading} onClick={load}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
            <Button variant="secondary" disabled={!data} onClick={exportCsv}>Download CSV</Button>
            {isCouncillor && (
              <Button
                className="border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800"
                onClick={() => setNotifyOpen(true)}
              >
                Notify residents
              </Button>
            )}
          </>
        }
      />

      {error && <div role="alert" className="rounded bg-red-50 p-3 text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded bg-green-50 p-3 text-green-800">{notice}</div>}

      {data && <>
        {/* KPIs */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Open cases" value={open.length} sub={`${reports.length} total`} />
          <Kpi label="Resolution rate" value={`${resolvedRate}%`} sub={`${resolved.length} resolved`} tone={resolvedRate >= 70 ? 'text-green-700' : undefined} />
          <Kpi label="SLA breached / at risk" value={`${breached.length} / ${(data.overdue || []).length}`} tone={breached.length ? 'text-red-700' : 'text-gray-900 dark:text-gray-100'} />
          <Kpi label="Recorded spend" value={`R ${Number(data.totalCost || 0).toLocaleString()}`} />
        </div>

        {/* trend */}
        <section className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold">Reports over the last 12 months</h2>
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-200" /> Reported</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-green-500" /> Resolved</span>
            </div>
          </div>
          <TrendChart monthly={data.monthly} />
        </section>

        {/* SLA risk board */}
        {(data.overdue || []).length > 0 && (
          <section className="rounded-lg border border-red-200 bg-red-50 p-4">
            <h2 className="font-semibold text-red-800">SLA attention needed</h2>
            <ul className="mt-2 divide-y divide-red-100">
              {data.overdue.map((o: any) => (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span><b>{o.report_number}</b> · {o.title}</span>
                  <span className={o.breached_at || new Date(o.resolution_due_at) < new Date() ? 'font-medium text-red-700' : 'text-amber-700'}>
                    {o.breached_at || new Date(o.resolution_due_at) < new Date()
                      ? `Breached ${new Date(o.breached_at || o.resolution_due_at).toLocaleDateString()}`
                      : `Due ${new Date(o.resolution_due_at).toLocaleString()}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* case table */}
        <section className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
            <h2 className="font-semibold">Ward cases</h2>
            <div className="flex flex-wrap gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reference, issue, address…"
                aria-label="Search cases" className="w-64 rounded border px-3 py-2 text-sm" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status"
                className="rounded border px-3 py-2 text-sm capitalize">
                <option value="all">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3">Reference</th><th className="p-3">Issue</th><th className="p-3">Priority</th>
                  <th className="p-3">Status</th><th className="p-3">Reported</th><th className="p-3">Follow-ups</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r: any) => {
                  const fups = followupsFor(r.id);
                  const isOpen = expanded === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="cursor-pointer border-t hover:bg-blue-50/40" onClick={() => setExpanded(isOpen ? '' : r.id)}
                        aria-expanded={isOpen}>
                        <td className="p-3 font-medium">{r.report_number || '—'}</td>
                        <td className="p-3">{r.title}</td>
                        <td className="p-3 capitalize">{r.priority || 'normal'}</td>
                        <td className="p-3"><StatusChip status={r.status} /></td>
                        <td className="p-3 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="p-3">{fups.length > 0 && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">{fups.length}</span>}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t bg-gray-50">
                          <td colSpan={6} className="p-4">
                            {r.description && <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">{r.description}</p>}
                            {r.address && <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{r.address}</p>}
                            {fups.length > 0 && (
                              <ul className="mb-3 space-y-2">
                                {fups.map((f) => (
                                  <li key={f.id} className="rounded border bg-white p-3 text-sm">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <span className="font-medium capitalize">{String(f.type).replaceAll('_', ' ')}</span>
                                      <span className={`rounded-full px-2 py-0.5 text-xs ${f.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{f.status}</span>
                                    </div>
                                    <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-300">{f.body}</p>
                                    <p className="mt-1 text-xs text-gray-400">{new Date(f.created_at).toLocaleString()}</p>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {isCouncillor ? (
                              <div className="flex flex-wrap items-end gap-2">
                                <label className="text-xs text-gray-600 dark:text-gray-400">Type
                                  <select value={followupDraft.type} onChange={(e) => setFollowupDraft({ ...followupDraft, type: e.target.value })}
                                    className="mt-1 block rounded border px-2 py-2 text-sm">
                                    {FOLLOWUP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                  </select>
                                </label>
                                <input value={followupDraft.body} onChange={(e) => setFollowupDraft({ ...followupDraft, body: e.target.value })}
                                  placeholder="Add a follow-up on this case…" aria-label="Follow-up note"
                                  className="min-w-[240px] flex-1 rounded border px-3 py-2 text-sm" />
                                <button onClick={() => addFollowup(r.id)} disabled={savingFollowup || !followupDraft.body.trim()}
                                  className="rounded bg-blue-700 px-4 py-2 text-sm text-white hover:bg-blue-800 disabled:opacity-50">
                                  {savingFollowup ? 'Saving…' : 'Add follow-up'}
                                </button>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">Follow-ups can only be created by the ward councillor.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!visible.length && <tr><td colSpan={6} className="p-6 text-center text-gray-500 dark:text-gray-400">No cases match this view.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* open followups */}
          <section className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="font-semibold">Open follow-ups ({openFollowups.length})</h2>
            <ul className="mt-3 space-y-2">
              {openFollowups.map((f) => (
                <li key={f.id} className="rounded border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{f.report_number} · {f.report_title}</span>
                    <StatusChip status={f.report_status} />
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-300">{f.body}</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => setFollowupStatus(f.id, 'responded')} className="rounded border px-3 py-1 text-xs hover:bg-gray-50">Mark responded</button>
                    <button onClick={() => setFollowupStatus(f.id, 'closed')} className="rounded border px-3 py-1 text-xs hover:bg-gray-50">Close</button>
                  </div>
                </li>
              ))}
              {!openFollowups.length && <p className="text-sm text-gray-500 dark:text-gray-400">No follow-ups are waiting on a response.</p>}
            </ul>
          </section>

          {/* outages */}
          <section className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="font-semibold">Current service interruptions</h2>
            <div className="mt-3 space-y-3">
              {data.outages?.map((o: any) => (
                <div key={o.id} className="rounded border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <b>{o.title}</b>
                    <span className="text-xs uppercase text-orange-700">{o.status}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{o.description}</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {new Date(o.starts_at).toLocaleString()}{o.estimated_end_at ? ` → est. ${new Date(o.estimated_end_at).toLocaleString()}` : ''}
                  </p>
                </div>
              ))}
              {!data.outages?.length && <p className="text-sm text-gray-500 dark:text-gray-400">No active or planned outages affecting this ward.</p>}
            </div>
          </section>
        </div>
      </>}

      {/* notify residents modal */}
      {notifyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Notify ward residents">
          <div className="w-full max-w-lg rounded-sm bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold">Notify ward {data?.wardNumber} residents</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sends a push notification to every registered resident of your ward. Use for outage updates, meetings and important ward news.</p>
            <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">Title
              <input value={notify.title} onChange={(e) => setNotify({ ...notify, title: e.target.value })} maxLength={80}
                className="mt-1 w-full rounded border px-3 py-2" placeholder="e.g. Water interruption update" />
            </label>
            <label className="mt-3 block text-sm font-medium text-gray-700 dark:text-gray-300">Message
              <textarea value={notify.body} onChange={(e) => setNotify({ ...notify, body: e.target.value })} maxLength={500} rows={4}
                className="mt-1 w-full rounded border px-3 py-2" placeholder="Keep it short and clear." />
            </label>
            <p className="mt-1 text-right text-xs text-gray-400">{notify.body.length}/500</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setNotifyOpen(false)} className="rounded border px-4 py-2 hover:bg-gray-50">Cancel</button>
              <button onClick={sendNotification} disabled={sending || !notify.title.trim() || !notify.body.trim()}
                className="rounded bg-green-700 px-4 py-2 text-white hover:bg-green-800 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WardCouncillorPortal;
